import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';

import {
  useSendVerificationEmailMutation,
  useUpdateUserMutation,
  useUserQuery,
  useUpdateUserConfirmMutation
} from '../graphql/types-and-hooks';
import { IntToLocalPhone } from '../utils/phone';
import { logError } from '../components/logger'; 
import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { getCachedConfig } from '../utils/configManager';
dayjs.extend(customParseFormat);

const EditProfileModal = ({ isOpen, onClose, userData1}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [isPhoneVerificationModalOpen, setIsPhoneVerificationModalOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  

  const { data: userData } = useUserQuery({});

  // eslint-disable-next-line 
  const [updateUserMutation, { loading: updateUserLoading }] =
    useUpdateUserMutation({
      refetchQueries: ['User', 'RegisteredClinics', 'UpcomingAppointments'],
      awaitRefetchQueries: true,
    });

  const [
    // eslint-disable-next-line 
    sendVerificationEmailMutation,{ loading: sendVerificationEmailLoading },
  ] = useSendVerificationEmailMutation();

  // eslint-disable-next-line 
  const formatDate = (dateString) => {
    return dayjs(dateString).format('DD/MM/YYYY');
  };

  const formatDateToSubmit = (dateString) => {
    // Convert from DD/MM/YYYY to YYYY-MM-DD for input type="date"
    const parsedDate = dayjs(dateString, 'DD/MM/YYYY');
    
      return parsedDate.format('YYYY-MM-DD'); // Output: 1990-05-14
    
  };

  useEffect(() => {
    if (isOpen) {
        // Send a message to the parent window to scroll to the top
        window.parent.postMessage({ action: 'scrollToTop' }, '*');
    }
}, [isOpen]);

const [config, setConfig] = useState<any>(null);
  const colors = config?.colorScheme || {};

  useEffect(() => {
    const config = getCachedConfig();
    if (config) {
      setConfig(config);
    }
  }, []);


  // eslint-disable-next-line 
  const [updateUserConfirmMutation, { loading: updateUserConfirmLoading }] =
    useUpdateUserConfirmMutation({
      refetchQueries: ['User', 'UpcomingAppointments'],
      awaitRefetchQueries: true,
    });

  useEffect(() => {
    if (userData1 && userData1.user) {
        
      setFirstName(userData1.user.first_name || '');
      setLastName(userData1.user.last_name || '');
      setDob( formatDateToSubmit(userData1.user.dob)|| '');
      //setDob(dayjs(userData1.user.dob, 'DD/MM/YYYY').format('YYYY-MM-DD') || '');
      setGender(userData1.user.gender || '');
      setEmail(userData1.user.email || '');
      setPhoneNumber(userData1.user.phone_number || '');
    }
  }, [userData1]);

  const handleSave = async () => {
    //const updatedData = { firstName, lastName, dob, gender, email, phoneNumber };
   
    const formattedDob = dob ? dayjs(dob, 'YYYY-MM-DD').format('DD/MM/YYYY') : null;

    try {
      if (!userData) {
        throw new Error();
      }

      if (userData?.user?.email_verified !== 'true') {
        setAlertMessage('Your email is not verified. Please verify your email before making changes.');
        setShowAlert(true);
        return;
      }

      const existingEmailAddress = userData?.user?.email;

      if (
        existingEmailAddress !== email &&
        (userData?.user?.first_name !== firstName ||
          userData?.user?.last_name !== lastName ||
          userData?.user?.dob !== formattedDob||
          userData?.user.gender !== gender ||
          userData.user?.phone_number !== phoneNumber)
      ) {
        setAlertMessage('You cannot change other details while changing your email address.');
        setShowAlert(true);
        return;
      }

      const existingPhoneNumber = userData?.user?.phone_number;

      const result = await updateUserMutation({
        variables: {
          input: {
            username: userData?.user?.username,
            first_name: firstName,
            last_name: lastName,
            dob: formattedDob,
            gender: gender,
            email: email,
            phone_number: phoneNumber,
          },
        },
      });

      if (result?.data?.updateUser?.success) {
        if (IntToLocalPhone(existingPhoneNumber) !== IntToLocalPhone(phoneNumber)) {
            setIsPhoneVerificationModalOpen(true);
        } else {
          setAlertMessage('Profile updated successfully.');
          setShowAlert(true);

          if (existingEmailAddress !== email) {
            handleEmailVerification();
          }
        }
      }
    } catch (error) {
      logError.error( 'editProfileSaveError', error); 
      setAlertMessage('An error occurred while updating your profile. Please try again.');
      setShowAlert(true);
    }

    //onClose();
  };

  const handleEmailVerification = async () => {
    try {
      const result = await sendVerificationEmailMutation({
        variables: {
          input: {
            oldEmail: userData?.user?.email,
            newEmail: email,
          },
        },
      });

      if (!result?.data?.sendVerificationEmail?.success) {
        throw new Error();
      }

      setAlertMessage('Verification email sent successfully.');
      setShowAlert(true);
    } catch (error) {
      logError.error(error, 'editProfileEmailVerificationError');
      setAlertMessage('An error occurred while sending the verification email. Please try again.');
      setShowAlert(true);
    }
  };

  const handlePhoneVerification = async () => { 
    try {
      await updateUserConfirmMutation({ 
        variables: {
          input: {
            phone_number: phoneNumber,
            code: verificationCode,
          },
        },
      });
  
      setIsPhoneVerificationModalOpen(false);
      setAlertMessage('Phone number verified successfully.');
      setShowAlert(true);
    } catch (error) {
      logError.error('editProfilePhoneVerificationError',error);
      setAlertMessage('Your verification code is not correct. Please verify your code.');
      setShowAlert(true);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} blockScrollOnMount={false} closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Profile</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {showAlert && (
              <Alert status="info" mb={4}>
                <AlertIcon />
                <AlertTitle mr={2}>Notice</AlertTitle>
                <AlertDescription>{alertMessage}</AlertDescription>
              </Alert>
            )}
            <FormControl>
              <FormLabel>First Name</FormLabel>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} borderRadius={'full'} />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel>Last Name</FormLabel>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} borderRadius={'full'} />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel>Date of Birth  </FormLabel>
              <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} borderRadius={'full'} />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel>Gender</FormLabel>
              <Input value={gender} onChange={(e) => setGender(e.target.value)} borderRadius={'full'} />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel>Email Address</FormLabel>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} borderRadius={'full'} />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel>Mobile Number</FormLabel>
              <Input value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} borderRadius={'full'} />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button w={'100%'} variant="ghost" onClick={onClose} borderRadius={'full'}>Cancel</Button>
            <Button w={'100%'} bg={colors.buttonBg || "#B92F2F"} color="#fff" ml={3} onClick={handleSave} borderRadius={'full'}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isPhoneVerificationModalOpen} onClose={() => setIsPhoneVerificationModalOpen(false)}  blockScrollOnMount={false} closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Verify Phone Number</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Verification Code</FormLabel>
              <Input value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} borderRadius={'full'} />
            </FormControl>
          </ModalBody>
          <ModalFooter >
            
            <Button variant="ghost" w={'100%'} onClick={() => setIsPhoneVerificationModalOpen(false)} borderRadius={'full'}>Cancel</Button>
            <Button  w={'100%'} bg={colors.buttonBg || "#B92F2F"} color="#fff" ml={3} onClick={handlePhoneVerification} borderRadius={'full'}>Verify</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default EditProfileModal;
